import React, { useEffect, useState, useContext } from "react";
import { 
  Table, 
  Button, 
  message, 
  Input, 
  Row, 
  Col, 
  Space, 
  Card, 
  Typography, 
  Divider,
  Tag,
  Tooltip
} from "antd";
import { 
  SearchOutlined, 
  DownloadOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserOutlined,
  FileTextOutlined 
} from "@ant-design/icons";
import axios from "axios";
import AppAdminLayout from "./components/AdminLayout";
import { LanguageContext } from "./LanguageContext";
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const CustomerList = () => {
  const [users, setUsers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchUserName, setSearchUserName] = useState("");
  const [searchCustomerId, setSearchCustomerId] = useState("");
  const [loading, setLoading] = useState(false);
  const { language, setLanguage, translations } = useContext(LanguageContext);
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [filteredConfigs, setFilteredConfigs] = useState([]);

  useEffect(() => {
    setLoading(true);
    axios
      .get("https://json-configrator-api.root.myoctoopidev.com/Customer/GetAllUserDetail")
      .then((response) => {
        if (response.data.success) {
          setUsers(response.data.data);
          // Tüm kullanıcıların müşterilerini birleştirerek tek bir liste oluştur
          const allCustomers = response.data.data.reduce((acc, user) => {
            return acc.concat(user.customers.map(customer => ({
              ...customer,
              userName: user.userName,
              userId: user.userId
            })));
          }, []);
          setFilteredData(allCustomers);
        } else {
          message.error("Veri alınırken hata oluştu.");
        }
      })
      .catch(() => {
        message.error("API isteği başarısız.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const filtered = users.reduce((acc, user) => {
      const userCustomers = user.customers
        .filter(customer => 
          user.userName.toLowerCase().includes(searchUserName.toLowerCase()) &&
          (customer.userId || "").toLowerCase().includes(searchCustomerId.toLowerCase())
        )
        .map(customer => ({
          ...customer,
          userName: user.userName
        }));
      return acc.concat(userCustomers);
    }, []);
    
    setFilteredData(filtered);
  }, [searchUserName, searchCustomerId, users]);

  const downloadConfig = (configString, customerId) => {
    try {
      const configJson = JSON.parse(configString);
      const blob = new Blob([JSON.stringify(configJson, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${customerId}_config.json`;
      link.click();
      URL.revokeObjectURL(url);
      message.success("Config dosyası başarıyla indirildi!");
    } catch (error) {
      message.error("Config verisi geçersiz JSON formatında.");
    }
  };

  const handleDelete = (record) => {
    axios
      .delete(
        "https://json-configrator-api.root.myoctoopidev.com/Customer/Delete",
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { id: record.id },
        }
      )
      .then((res) => {
        if (res.data.success) {
          message.success(translations[language].succes);
          setConfigs((prev) => prev.filter((cfg) => cfg.id !== record.id));
          setFilteredConfigs((prev) =>
            prev.filter((cfg) => cfg.id !== record.id)
          );
          setFilteredData((prev) =>
            prev.filter((item) => item.id !== record.id)
          );
        } else {
          message.error("Silme işlemi başarısız.");
        }
      })
      .catch(() => {
        message.error("API isteği başarısız.");
      });
  };

  const columns = [
    {
      title: `${translations[language].ka}`,
      dataIndex: "userName",
      key: "userName",
      render: (userName) => (
        <Space>
          <UserOutlined style={{ color: "#1890ff" }} />
          <Text strong>{userName}</Text>
        </Space>
      )
    },
    {
      title: `${translations[language].mid}`,
      dataIndex: "userId",
      key: "userId",
      render: (userId) => <Tag color="green">{userId}</Tag>
    },
    {
      title: `${translations[language].kag}`,
      dataIndex: "configName",
      key: "configName",
      render: (configName) => (
        <Space>
          <FileTextOutlined />
          <Text>{configName}</Text>
        </Space>
      )
    },
    {
      title: `${translations[language].fileOperation}`,
      key: "actions",
      width: 250,
      render: (record) => (
        <Space size="small">
          <Tooltip title={translations[language].kdi}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => downloadConfig(record.config, record.id)}
            />
          </Tooltip>
          <Tooltip title={translations[language].showFiles}>
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() =>
                navigate("/adminforselfupdateeditablejson", {
                  state: { configString: record.config, configId: record.id },
                })
              }
            />
          </Tooltip>
          <Tooltip title={translations[language].removeFilses}>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <AppAdminLayout>
      <div style={{ padding: "24px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
        <Card 
          style={{ 
            borderRadius: "12px", 
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid #e8e8e8"
          }}
        >
          {/* Header Section */}
          <Row justify="space-between" align="middle" style={{ marginBottom: "24px" }}>
            <Col>
              <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                Müşteri Listesi
              </Title>
              <Text type="secondary">
                {filteredData.length} müşteri config'i bulundu
              </Text>
            </Col>
          </Row>

          <Divider style={{ margin: "0 0 24px 0" }} />

          {/* Search Section */}
          <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder={translations[language].kaa}
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={searchUserName}
                onChange={(e) => setSearchUserName(e.target.value)}
                size="large"
                style={{ borderRadius: "8px" }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder={translations[language].mia}
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={searchCustomerId}
                onChange={(e) => setSearchCustomerId(e.target.value)}
                size="large"
                style={{ borderRadius: "8px" }}
                allowClear
              />
            </Col>
          </Row>

          {/* Table Section */}
          <Table 
            rowKey="id" 
            dataSource={filteredData} 
            columns={columns} 
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} items`
            }}
            bordered
            style={{
              borderRadius: "8px",
              overflow: "hidden"
            }}
            rowClassName={(record, index) => 
              index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
            }
          />
        </Card>
      </div>

      <style jsx>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .ant-table-thead > tr > th {
          background-color: #f0f2f5;
          font-weight: 600;
        }
      `}</style>
    </AppAdminLayout>
  );
};

export default CustomerList;